import { useState } from 'react';
import { Navbar } from '@/components/layout/Navbar';
import { Cloud, Car, MapPin, Navigation, RefreshCw, AlertTriangle, Bot } from 'lucide-react';
import OceanBackground from '@/components/background/OceanBackground';

const MOCK_WEATHER = { temp: 24, condition: '多云', rain: 35, hourly: [{ h: '14:00', t: 25 }, { h: '15:00', t: 24 }, { h: '16:00', t: 23 }, { h: '17:00', t: 22 }] };
const MOCK_NEXT = { name: '灵隐寺', distance: '3.2km', eta: '14:40', transport: '打车 12分钟', note: '飞来峰景区内步行较多，建议穿舒适鞋履' };
const MOCK_AI = [
  { text: '行程正常。下一站灵隐寺预计 14:00 出发。', type: 'status' },
  { text: '下午降雨概率 35%，建议 14:00 前出发。', type: 'suggestion' },
];

const TIMELINE = [
  { time: '08:30', title: '酒店早餐', done: true, detail: '西湖国宾馆·自助餐厅' },
  { time: '09:30', title: '断桥残雪', done: true, detail: '游览 45分钟' },
  { time: '10:00', title: '白堤骑行', done: true, detail: '租自行车 30分钟' },
  { time: '12:10', title: '午餐', done: true, detail: '外婆家·西湖店' },
  { time: '14:00', title: '前往灵隐寺', next: true, detail: '打车 12分钟' },
  { time: '14:30', title: '灵隐寺', done: false, detail: '游览 1.5小时' },
  { time: '17:30', title: '晚餐', done: false, detail: '法云安缦·茶餐厅' },
];

const LiveTrip = () => {
  const [showReplan, setShowReplan] = useState(false);

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(180deg, #041426 0%, #061A2C 50%, #041426 100%)' }}>
      <Navbar />
      <OceanBackground />
      <div className="max-w-6xl mx-auto px-6 pt-24 pb-20 relative z-10">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
            <span className="text-xs font-medium" style={{ color: 'rgba(57,201,130,0.9)' }}>行程进行中</span>
            <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'rgba(74,167,216,0.12)', color: 'rgba(74,167,216,0.8)' }}>Day 2 / 4</span>
          </div>
          <h1 className="text-2xl font-semibold mb-1" style={{ color: 'rgba(255,255,255,0.94)', letterSpacing: '-0.025em' }}>杭州 · 实时旅行</h1>
          <p className="text-sm" style={{ color: 'rgba(255,255,255,0.5)' }}>9月16日 · 晴转多云 · 24°C</p>
        </div>

        {/* Warning */}
        {showReplan && (
          <div className="ocean-glass p-4 mb-6" style={{ borderColor: 'rgba(232,168,56,0.2)' }}>
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#E8A838' }} />
              <div className="flex-1">
                <p className="text-sm font-medium mb-1" style={{ color: 'rgba(255,255,255,0.9)' }}>检测到异常天气</p>
                <p className="text-xs mb-3" style={{ color: 'rgba(255,255,255,0.6)' }}>今日下午降雨概率 35%，灵隐寺为户外景点，建议调整</p>
                <div className="rounded-lg p-3" style={{ background: 'rgba(232,168,56,0.08)', border: '1px solid rgba(232,168,56,0.15)' }}>
                  <p className="text-xs font-medium mb-1" style={{ color: 'rgba(255,255,255,0.85)' }}>已生成替代方案：</p>
                  <p className="text-xs" style={{ color: 'rgba(255,255,255,0.6)' }}>灵隐寺 → 中国丝绸博物馆（室内） → 浙江省博物馆</p>
                </div>
              </div>
              <button onClick={() => setShowReplan(false)} className="text-xs px-3 py-1.5 rounded-lg flex-shrink-0" style={{ background: 'rgba(232,168,56,0.2)', color: '#E8A838' }}>
                确认切换
              </button>
            </div>
          </div>
        )}

        {/* Main Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
          {/* Timeline (主视觉) */}
          <div className="lg:col-span-5">
            <div className="ocean-glass p-5">
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-sm font-semibold" style={{ color: 'rgba(255,255,255,0.9)' }}>今天</h2>
                <button onClick={() => setShowReplan(!showReplan)} className="text-xs px-3 py-1.5 rounded-lg flex items-center gap-1.5 transition-all duration-200" style={{ background: 'rgba(74,167,216,0.1)', color: 'rgba(74,167,216,0.85)' }}>
                  <RefreshCw className="w-3 h-3" /> 重新规划
                </button>
              </div>
              <div className="space-y-0">
                {TIMELINE.map((item, i) => (
                  <div key={i} className="flex gap-3.5">
                    <div className="flex flex-col items-center">
                      <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                        item.done ? 'bg-green-400' : item.next ? 'ring-2 ring-blue-400 ring-offset-1' : ''
                      }`} style={!item.done && !item.next ? { background: 'rgba(255,255,255,0.15)' } : item.next ? { background: '#4AA7D8' } : {}} />
                      {i < TIMELINE.length - 1 && (
                        <div className="w-px flex-1 my-1" style={{ background: item.done ? 'rgba(57,201,130,0.3)' : 'rgba(255,255,255,0.08)' }} />
                      )}
                    </div>
                    <div className={`pb-4 flex-1 ${!item.done && !item.next ? 'opacity-40' : ''}`}>
                      <div className="flex items-center justify-between">
                        <span className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>{item.time}</span>
                        {item.next && <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(74,167,216,0.15)', color: '#4AA7D8' }}>当前</span>}
                      </div>
                      <p className={`text-sm mt-0.5 ${item.next ? 'font-medium' : ''}`} style={{ color: item.next ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.7)' }}>{item.title}</p>
                      <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.4)' }}>{item.detail}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Center: Next Stop + Weather */}
          <div className="lg:col-span-3 space-y-4">
            <div className="ocean-glass p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold" style={{ color: 'rgba(255,255,255,0.9)' }}>下一站</h3>
                <Navigation className="w-3.5 h-3.5" style={{ color: '#4AA7D8' }} />
              </div>
              <div className="rounded-xl p-4 mb-3" style={{ background: 'linear-gradient(135deg, rgba(74,167,216,0.08), rgba(216,177,90,0.06))' }}>
                <p className="text-lg font-semibold" style={{ color: 'rgba(255,255,255,0.95)' }}>{MOCK_NEXT.name}</p>
                <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.5)' }}>{MOCK_NEXT.distance} · 预计 {MOCK_NEXT.eta}</p>
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-xs" style={{ color: 'rgba(255,255,255,0.6)' }}>
                  <Car className="w-3.5 h-3.5" style={{ color: '#4AA7D8' }} />
                  <span>{MOCK_NEXT.transport}</span>
                </div>
                <div className="flex items-center gap-2 text-xs" style={{ color: 'rgba(255,255,255,0.6)' }}>
                  <MapPin className="w-3.5 h-3.5" style={{ color: '#4AA7D8' }} />
                  <span>{MOCK_NEXT.note}</span>
                </div>
              </div>
            </div>

            <div className="ocean-glass p-5">
              <h3 className="text-sm font-semibold mb-3" style={{ color: 'rgba(255,255,255,0.9)' }}>实时天气</h3>
              <div className="flex items-center gap-4 mb-3">
                <div className="text-3xl font-light" style={{ color: 'rgba(255,255,255,0.95)' }}>{MOCK_WEATHER.temp}°</div>
                <div>
                  <p className="text-sm" style={{ color: 'rgba(255,255,255,0.8)' }}>{MOCK_WEATHER.condition}</p>
                  <p className="text-xs" style={{ color: 'rgba(255,255,255,0.45)' }}>降雨概率 {MOCK_WEATHER.rain}%</p>
                </div>
              </div>
              <div className="flex gap-2">
                {MOCK_WEATHER.hourly.map((h, i) => (
                  <div key={i} className="flex-1 text-center rounded-lg p-2" style={{ background: 'rgba(255,255,255,0.04)' }}>
                    <p className="text-[10px]" style={{ color: 'rgba(255,255,255,0.35)' }}>{h.h}</p>
                    <p className="text-sm font-medium" style={{ color: 'rgba(255,255,255,0.8)' }}>{h.t}°</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right: AI Copilot */}
          <div className="lg:col-span-4">
            <div className="ocean-glass p-5 h-full flex flex-col">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: 'rgba(74,167,216,0.15)' }}>
                  <Bot className="w-3.5 h-3.5" style={{ color: '#4AA7D8' }} />
                </div>
                <h3 className="text-sm font-semibold" style={{ color: 'rgba(255,255,255,0.9)' }}>AI Copilot</h3>
                <span className="text-[10px] px-1.5 py-0.5 rounded-full ml-auto" style={{ background: 'rgba(57,201,130,0.12)', color: '#39C982' }}>● 行程正常</span>
              </div>
              <div className="flex-1 space-y-3 mb-4">
                {MOCK_AI.map((msg, i) => (
                  <div key={i} className="rounded-xl p-3" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}>
                    <p className="text-xs leading-relaxed" style={{ color: 'rgba(255,255,255,0.7)' }}>{msg.text}</p>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                {['重新规划', '附近推荐', '延长行程'].map(q => (
                  <button key={q} className="flex-1 text-[11px] py-2 rounded-lg transition-all duration-200" style={{ background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.6)', border: '1px solid rgba(255,255,255,0.08)' }}>
                    {q}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiveTrip;
